import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { AiAssistantSettings, EmployeeTemplate } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/utils";

type Row = {
  key: string;
  navigateTo: string;
  name: string;
  subtitle: string;
  type: string;
  active: boolean;
  version: string;
  updatedAt: string | null;
  updatedBy: string | null;
};

export function PromptsListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("templates");

  const aiQuery = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => api.get<{ settings: AiAssistantSettings }>("/api/admin/ai-settings"),
  });

  const templatesQuery = useQuery({
    queryKey: ["employee-templates"],
    queryFn: () => api.get<{ templates: EmployeeTemplate[] }>("/api/admin/employee-templates"),
  });

  const isLoading = aiQuery.isLoading || templatesQuery.isLoading;
  const error = aiQuery.error || templatesQuery.error;

  const rows: Row[] = React.useMemo(() => {
    const out: Row[] = [];
    if (aiQuery.data?.settings) {
      const s = aiQuery.data.settings;
      out.push({
        key: "ai-assistant",
        navigateTo: "/templates/ai-assistant",
        name: t("list.aiAssistant.name"),
        subtitle: t("list.aiAssistant.subtitle"),
        type: "system",
        active: s.enabled,
        version: "—",
        updatedAt: s.updatedAt,
        updatedBy: s.updatedBy,
      });
    }
    for (const tpl of templatesQuery.data?.templates ?? []) {
      out.push({
        key: tpl.id,
        navigateTo: `/templates/employee/${tpl.id}`,
        name: tpl.name || tpl.templateId,
        subtitle: tpl.title || tpl.description || "",
        type: tpl.employeeType || "—",
        active: tpl.isActive,
        version: tpl.promptGeneratorVersion || "—",
        updatedAt: tpl.updatedAt,
        updatedBy: tpl.updatedBy,
      });
    }
    return out;
  }, [aiQuery.data, templatesQuery.data, t]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("list.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("list.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("list.cardTitle")}</CardTitle>
          <CardDescription>
            {t("list.cardDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">
              {t("errors.loadFailed", {
                message: error instanceof Error ? error.message : String(error),
              })}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("list.columns.name")}</TableHead>
                  <TableHead>{t("list.columns.type")}</TableHead>
                  <TableHead>{t("list.columns.status")}</TableHead>
                  <TableHead>{t("list.columns.version")}</TableHead>
                  <TableHead>{t("list.columns.lastUpdated")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.key}
                    className="cursor-pointer"
                    onClick={() => navigate(r.navigateTo)}
                  >
                    <TableCell>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.subtitle}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.type === "system" ? "secondary" : "outline"}>
                        {r.type === "system" ? t("list.typeSystem") : r.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.active ? "success" : "muted"}>
                        {r.active ? t("status.active") : t("status.inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.version}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(r.updatedAt)}
                      {r.updatedBy ? ` · ${r.updatedBy}` : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
